import {
  SCORECARD_DEFAULT_ASSERT_VALID_TYPE,
  SCORECARD_DEFAULT_CODE,
  SCORECARD_DEFAULT_CODE_BY_LANGUAGE,
  SCORECARD_DEFAULT_LLM_ASSERTION_PROMPT,
  SCORECARD_DEFAULT_REGEX_PATTERN,
} from "./constants";

import { ScorecardPrimitiveType } from "@/queries/smart-tables/scorecard";

export const AGENT_TRACE_PRIMITIVE_TYPES = new Set<string>([
  ScorecardPrimitiveType.MAX_TOOL_CALLS,
  ScorecardPrimitiveType.NO_INVALID_TOOL_CALLS,
  ScorecardPrimitiveType.TOOL_EVALUATOR,
  ScorecardPrimitiveType.TRAJECTORY,
]);

export const COLUMN_AGGREGATE_TYPE_LABELS = {
  average_value: "Average numeric value",
  most_frequent_value: "Most frequent value",
  min_value: "Lowest numeric value",
  max_value: "Highest numeric value",
  percentile_value: "Percentile",
  mean_absolute_error: "Mean absolute error (MAE)",
  root_mean_squared_error: "Root mean squared error (RMSE)",
  classification_report: "Classification report",
} as const;
export type ColumnAggregateType = keyof typeof COLUMN_AGGREGATE_TYPE_LABELS;
export type ColumnAggregateScoreMode = "value" | "score";
export const DEFAULT_COLUMN_AGGREGATE_PERCENTILE = 95;

/** A percentile between 0 (exclusive) and 100, or undefined when the input is not one. */
export const parsePercentile = (value: string): number | undefined => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= 100 ? parsed : undefined;
};

export const isColumnAggregateType = (value: unknown): value is ColumnAggregateType =>
  typeof value === "string" && value in COLUMN_AGGREGATE_TYPE_LABELS;

/** Types that compare the column against an expected column on the same row. */
export const columnAggregateUsesExpectedColumn = (type: ColumnAggregateType): boolean =>
  type === "mean_absolute_error" ||
  type === "root_mean_squared_error" ||
  type === "classification_report";

/** Types whose result can count toward the scorecard score; the rest only report a value. */
export const columnAggregateCanScore = (type: ColumnAggregateType): boolean =>
  type === "average_value" || type === "most_frequent_value" || type === "classification_report";
export type CriterionComparisonValueMode = "static" | "column";
export type ScorecardCodeLanguage = keyof typeof SCORECARD_DEFAULT_CODE_BY_LANGUAGE;
export type ScorecardCompareType = "STRING" | "NUMBER" | "JSON";
export type ScorecardCompareOperator = "EQ" | "NE" | "GT" | "GTE" | "LT" | "LTE";
export type ScorecardCountType = "chars" | "words" | "sentences" | "paragraphs";
export type TrajectoryExpectationMode = "tools" | "column";
export type TrajectoryMatchMode = "strict" | "non_strict";

interface CodeExecutionSourceColumn {
  id: string;
  title: string;
  system_kind?: string | null;
}

const SCORECARD_BACKING_TITLE_PREFIX = "[Scorecard] ";

const SOURCE_BINDING_KEYS = new Set([
  "source",
  "sources",
  "value_source",
  "prompt_source",
  "label_source",
  "target",
  "variable_mappings",
  "argument_mappings",
  "trace_source",
  "expected_source",
]);

export const isAgentTracePrimitive = (primitiveType: string): boolean =>
  AGENT_TRACE_PRIMITIVE_TYPES.has(primitiveType);

export const getCodeExecutionSourceColumnIds = (columns: CodeExecutionSourceColumn[]): string[] =>
  columns
    .filter(
      column =>
        column.system_kind !== "scorecard_step" &&
        !column.title.startsWith(SCORECARD_BACKING_TITLE_PREFIX),
    )
    .map(column => column.id);

export const getContainsComparisonMode = (
  primitiveConfig: Record<string, unknown>,
): CriterionComparisonValueMode => (primitiveConfig.value_source ? "column" : "static");

export const getContainsStaticValue = (primitiveConfig: Record<string, unknown>): string => {
  const { value } = primitiveConfig;
  return typeof value === "string" ? value : "";
};

export const getScorecardCompareTargetMode = (
  primitiveConfig: Record<string, unknown>,
): CriterionComparisonValueMode => (primitiveConfig.target !== undefined ? "static" : "column");

export const getScorecardCompareStaticTarget = (
  primitiveConfig: Record<string, unknown>,
): string => {
  const { target } = primitiveConfig;
  if (target === null || target === undefined) {
    return "";
  }
  return typeof target === "string" ? target : String(target);
};

const getComparisonTypeConfig = (
  primitiveConfig: Record<string, unknown>,
): Record<string, unknown> | null => {
  const { comparison_type: comparisonType } = primitiveConfig;
  return comparisonType && typeof comparisonType === "object"
    ? (comparisonType as Record<string, unknown>)
    : null;
};

export const getScorecardCompareType = (
  primitiveConfig: Record<string, unknown>,
): ScorecardCompareType => {
  const type = getComparisonTypeConfig(primitiveConfig)?.type;
  if (type === "JSON" || type === "NUMBER") {
    return type;
  }
  return "STRING";
};

export const getScorecardCompareJsonPath = (primitiveConfig: Record<string, unknown>): string => {
  const comparisonType = getComparisonTypeConfig(primitiveConfig);
  return typeof comparisonType?.json_path === "string" ? comparisonType.json_path : "$.*";
};

export const getScorecardCompareOperator = (
  primitiveConfig: Record<string, unknown>,
): ScorecardCompareOperator => {
  const operator = getComparisonTypeConfig(primitiveConfig)?.operator;
  if (
    operator === "EQ" ||
    operator === "NE" ||
    operator === "GT" ||
    operator === "GTE" ||
    operator === "LT" ||
    operator === "LTE"
  ) {
    return operator;
  }
  return "EQ";
};

const buildCompareComparisonTypeConfig = ({
  compareType,
  compareJsonPath,
  compareOperator,
  stored,
}: {
  compareType: ScorecardCompareType;
  compareJsonPath?: string;
  compareOperator?: ScorecardCompareOperator;
  stored: Record<string, unknown>;
}): Record<string, unknown> => {
  if (compareType === "JSON") {
    return { type: "JSON", json_path: compareJsonPath || "$.*" };
  }
  if (compareType === "NUMBER") {
    return { type: "NUMBER", operator: compareOperator ?? getScorecardCompareOperator(stored) };
  }
  return { type: "STRING" };
};

export const extractStoredPrimitiveConfig = (
  primitiveConfig: Record<string, unknown> = {},
): Record<string, unknown> => {
  const stored: Record<string, unknown> = { ...primitiveConfig };
  SOURCE_BINDING_KEYS.forEach(key => {
    delete stored[key];
  });
  return stored;
};

const nonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

export const getScorecardDefaultCode = (language: ScorecardCodeLanguage): string =>
  SCORECARD_DEFAULT_CODE_BY_LANGUAGE[language];

const uniqueColumnIds = (columnIds: string[]): string[] => {
  const seen = new Set<string>();
  return columnIds.filter(columnId => {
    if (!columnId || seen.has(columnId)) {
      return false;
    }
    seen.add(columnId);
    return true;
  });
};

export const isScorecardDefaultCode = (code: string): boolean => {
  const normalizedCode = code.trim();
  return [SCORECARD_DEFAULT_CODE, ...Object.values(SCORECARD_DEFAULT_CODE_BY_LANGUAGE)].some(
    defaultCode => normalizedCode === defaultCode.trim(),
  );
};

const parseOptionalBound = (value: string | undefined): number | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
};

export const buildScorecardPrimitiveConfig = ({
  primitiveType,
  sourceColumnId,
  matchColumnId,
  staticValue,
  comparisonMode,
  maxCalls = 5,
  aggregateType = "average_value",
  aggregateScoreMode = "value",
  aggregateScoreMax = "1",
  labelColumnId,
  expectedColumnId,
  percentile,
  compareType,
  compareJsonPath,
  compareOperator,
  variableMappings = {},
  promptSourceColumnId,
  countType = "words",
  minCount,
  maxCount,
  trajectoryExpectationMode = "tools",
  expectedScenarios = [[]],
  trajectoryNoToolsExpected = false,
  trajectoryMatchMode = "strict",
  existingConfig = {},
}: {
  primitiveType: string;
  sourceColumnId: string;
  matchColumnId?: string;
  staticValue?: string;
  comparisonMode?: CriterionComparisonValueMode;
  maxCalls?: number;
  aggregateType?: ColumnAggregateType;
  aggregateScoreMode?: ColumnAggregateScoreMode;
  aggregateScoreMax?: string;
  labelColumnId?: string;
  expectedColumnId?: string;
  percentile?: string;
  compareType?: ScorecardCompareType;
  compareJsonPath?: string;
  compareOperator?: ScorecardCompareOperator;
  variableMappings?: Record<string, string>;
  promptSourceColumnId?: string;
  countType?: ScorecardCountType;
  minCount?: string;
  maxCount?: string;
  trajectoryExpectationMode?: TrajectoryExpectationMode;
  expectedScenarios?: string[][];
  trajectoryNoToolsExpected?: boolean;
  trajectoryMatchMode?: TrajectoryMatchMode;
  existingConfig?: Record<string, unknown>;
}): Record<string, unknown> => {
  const stored = extractStoredPrimitiveConfig(existingConfig);

  switch (primitiveType) {
    case ScorecardPrimitiveType.COLUMN_AGGREGATE: {
      const parsedScoreMax = Number.parseFloat(aggregateScoreMax);
      return {
        ...stored,
        source: sourceColumnId,
        aggregation_type: aggregateType,
        label_source:
          (aggregateType === "min_value" || aggregateType === "max_value") && labelColumnId
            ? labelColumnId
            : null,
        expected_source:
          columnAggregateUsesExpectedColumn(aggregateType) && expectedColumnId
            ? expectedColumnId
            : null,
        percentile:
          aggregateType === "percentile_value"
            ? (parsePercentile(percentile ?? "") ?? DEFAULT_COLUMN_AGGREGATE_PERCENTILE)
            : null,
        score_mode: columnAggregateCanScore(aggregateType) ? aggregateScoreMode : "value",
        score_max: Number.isFinite(parsedScoreMax) && parsedScoreMax > 0 ? parsedScoreMax : 1,
      };
    }
    case ScorecardPrimitiveType.CONTAINS:
      if (comparisonMode === "column" && matchColumnId) {
        return { ...stored, source: sourceColumnId, value_source: matchColumnId };
      }
      return { ...stored, source: sourceColumnId, value: staticValue ?? "" };
    case ScorecardPrimitiveType.COMPARE: {
      const resolvedCompareType = compareType ?? getScorecardCompareType(stored);
      const targetConfig =
        comparisonMode === "static"
          ? { sources: [sourceColumnId].filter(Boolean), target: staticValue ?? "" }
          : {
              sources: [sourceColumnId, matchColumnId].filter((id): id is string => Boolean(id)),
            };
      return {
        ...stored,
        ...targetConfig,
        comparison_type: buildCompareComparisonTypeConfig({
          compareType: resolvedCompareType,
          compareJsonPath,
          compareOperator,
          stored,
        }),
      };
    }
    case ScorecardPrimitiveType.MAX_TOOL_CALLS:
      return { ...stored, source: sourceColumnId, max_calls: maxCalls };
    case ScorecardPrimitiveType.NO_INVALID_TOOL_CALLS:
      return { ...stored, source: sourceColumnId };
    case ScorecardPrimitiveType.COUNT: {
      const parsedMin = parseOptionalBound(minCount);
      const parsedMax = parseOptionalBound(maxCount);
      return {
        ...stored,
        source: sourceColumnId,
        type: countType,
        min_count: parsedMin ?? null,
        max_count: parsedMax ?? null,
      };
    }
    case ScorecardPrimitiveType.TRAJECTORY: {
      const trajectoryConfig: Record<string, unknown> = {
        ...stored,
        trace_source: sourceColumnId,
        mode: trajectoryMatchMode,
      };
      delete trajectoryConfig.expected_tools;
      delete trajectoryConfig.expected_source;
      delete trajectoryConfig.scenarios;
      delete trajectoryConfig.accepted_scenarios;
      if (trajectoryExpectationMode === "column") {
        trajectoryConfig.expected_source = matchColumnId || "";
      } else {
        const normalizedScenarios = expectedScenarios
          .map(scenario => scenario.map(tool => tool.trim()).filter(Boolean))
          .filter(scenario => scenario.length > 0);
        trajectoryConfig.accepted_scenarios = trajectoryNoToolsExpected
          ? [[]]
          : normalizedScenarios;
      }
      return trajectoryConfig;
    }
    case ScorecardPrimitiveType.LLM_ASSERTION: {
      const promptConfig = { ...stored };
      if (promptSourceColumnId) {
        delete promptConfig.prompt;
        return {
          ...promptConfig,
          source: sourceColumnId,
          prompt_source: promptSourceColumnId,
        };
      }

      return {
        ...promptConfig,
        source: sourceColumnId,
        prompt: nonEmptyString(promptConfig.prompt)
          ? promptConfig.prompt
          : SCORECARD_DEFAULT_LLM_ASSERTION_PROMPT,
        variable_mappings: variableMappings,
      };
    }
    case ScorecardPrimitiveType.CODE_EXECUTION: {
      const language = stored.language === "JAVASCRIPT" ? "JAVASCRIPT" : "PYTHON";
      return {
        ...stored,
        code: nonEmptyString(stored.code) ? stored.code : getScorecardDefaultCode(language),
        language,
      };
    }
    case ScorecardPrimitiveType.REGEX:
      return {
        ...stored,
        source: sourceColumnId,
        regex_pattern: nonEmptyString(stored.regex_pattern)
          ? stored.regex_pattern
          : SCORECARD_DEFAULT_REGEX_PATTERN,
      };
    case ScorecardPrimitiveType.ASSERT_VALID:
      return {
        ...stored,
        source: sourceColumnId,
        type:
          stored.type === "object" || stored.type === "number" || stored.type === "sql"
            ? stored.type
            : SCORECARD_DEFAULT_ASSERT_VALID_TYPE,
      };
    default:
      return { ...stored, source: sourceColumnId };
  }
};

export const buildScorecardSourceColumnIds = ({
  primitiveType,
  sourceColumnId,
  matchColumnId,
  comparisonMode,
  codeExecutionSourceColumnIds = [],
  variableMappings = {},
  promptSourceColumnId,
}: {
  primitiveType: string;
  sourceColumnId: string;
  matchColumnId?: string;
  comparisonMode?: CriterionComparisonValueMode;
  codeExecutionSourceColumnIds?: string[];
  variableMappings?: Record<string, string>;
  promptSourceColumnId?: string;
}): string[] => {
  if (primitiveType === ScorecardPrimitiveType.COLUMN_AGGREGATE) {
    return [sourceColumnId, matchColumnId].filter((id): id is string => Boolean(id));
  }
  if (primitiveType === ScorecardPrimitiveType.COMPARE) {
    if (comparisonMode === "static") {
      return sourceColumnId ? [sourceColumnId] : [];
    }
    return [sourceColumnId, matchColumnId].filter((id): id is string => Boolean(id));
  }
  if (
    primitiveType === ScorecardPrimitiveType.CONTAINS &&
    comparisonMode === "column" &&
    matchColumnId
  ) {
    return [sourceColumnId, matchColumnId];
  }
  if (primitiveType === ScorecardPrimitiveType.CODE_EXECUTION) {
    return codeExecutionSourceColumnIds;
  }
  if (primitiveType === ScorecardPrimitiveType.LLM_ASSERTION) {
    return uniqueColumnIds([
      sourceColumnId,
      promptSourceColumnId ?? "",
      ...Object.values(variableMappings),
    ]);
  }
  if (primitiveType === ScorecardPrimitiveType.TRAJECTORY) {
    return [sourceColumnId, matchColumnId].filter((id): id is string => Boolean(id));
  }
  return sourceColumnId ? [sourceColumnId] : [];
};

export const getScorecardSourceColumnLabel = (primitiveType: string): string => {
  if (primitiveType === ScorecardPrimitiveType.COLUMN_AGGREGATE) {
    return "Column to use";
  }
  return isAgentTracePrimitive(primitiveType) ? "Agent trace column" : "Source column";
};

export const getScorecardColumnAggregateType = (
  primitiveConfig: Record<string, unknown>,
): ColumnAggregateType => {
  const { aggregation_type: aggregationType } = primitiveConfig;
  return isColumnAggregateType(aggregationType) ? aggregationType : "average_value";
};

export const getScorecardLabelSource = (primitiveConfig: Record<string, unknown>): string => {
  const { label_source: labelSource } = primitiveConfig;
  return typeof labelSource === "string" ? labelSource : "";
};

export const getScorecardExpectedSource = (primitiveConfig: Record<string, unknown>): string => {
  const { expected_source: expectedSource } = primitiveConfig;
  return typeof expectedSource === "string" ? expectedSource : "";
};

export const getScorecardPercentile = (primitiveConfig: Record<string, unknown>): string => {
  const { percentile } = primitiveConfig;
  return String(typeof percentile === "number" ? percentile : DEFAULT_COLUMN_AGGREGATE_PERCENTILE);
};

export const getScorecardColumnAggregateScoreMode = (
  primitiveConfig: Record<string, unknown>,
): ColumnAggregateScoreMode => (primitiveConfig.score_mode === "score" ? "score" : "value");

export const getScorecardColumnAggregateScoreMax = (
  primitiveConfig: Record<string, unknown>,
): string => {
  const { score_max: scoreMax } = primitiveConfig;
  if (typeof scoreMax === "number" && Number.isFinite(scoreMax) && scoreMax > 0) {
    return String(scoreMax);
  }
  return "1";
};

export const getScorecardPrompt = (primitiveConfig: Record<string, unknown>): string => {
  const { prompt } = primitiveConfig;
  return typeof prompt === "string" ? prompt : SCORECARD_DEFAULT_LLM_ASSERTION_PROMPT;
};

export const getScorecardPromptSource = (primitiveConfig: Record<string, unknown>): string => {
  const { prompt_source: promptSource } = primitiveConfig;
  return typeof promptSource === "string" ? promptSource : "";
};

export const getScorecardVariableMappings = (
  primitiveConfig: Record<string, unknown>,
): Record<string, string> => {
  const { variable_mappings: variableMappings } = primitiveConfig;
  if (
    !variableMappings ||
    typeof variableMappings !== "object" ||
    Array.isArray(variableMappings)
  ) {
    return {};
  }
  return Object.entries(variableMappings).reduce<Record<string, string>>((acc, [key, value]) => {
    if (typeof value === "string") {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const getScorecardCode = (
  primitiveConfig: Record<string, unknown>,
  language: ScorecardCodeLanguage = "PYTHON",
): string => {
  const { code } = primitiveConfig;
  return typeof code === "string" ? code : getScorecardDefaultCode(language);
};

export const getScorecardCodeLanguage = (
  primitiveConfig: Record<string, unknown>,
): ScorecardCodeLanguage => {
  const { language } = primitiveConfig;
  return language === "JAVASCRIPT" ? "JAVASCRIPT" : "PYTHON";
};

export const getScorecardRegexPattern = (primitiveConfig: Record<string, unknown>): string => {
  const { regex_pattern: regexPattern } = primitiveConfig;
  return typeof regexPattern === "string" ? regexPattern : SCORECARD_DEFAULT_REGEX_PATTERN;
};

export const getScorecardAssertValidType = (
  primitiveConfig: Record<string, unknown>,
): "object" | "number" | "sql" => {
  const { type } = primitiveConfig;
  if (type === "object" || type === "number" || type === "sql") {
    return type;
  }
  return SCORECARD_DEFAULT_ASSERT_VALID_TYPE;
};

export const getScorecardCountType = (
  primitiveConfig: Record<string, unknown>,
): ScorecardCountType => {
  const { type } = primitiveConfig;
  if (type === "chars" || type === "words" || type === "sentences" || type === "paragraphs") {
    return type;
  }
  return "words";
};

export const getScorecardCountBound = (
  primitiveConfig: Record<string, unknown>,
  key: "min_count" | "max_count",
): string => {
  const value = primitiveConfig[key];
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
};

export const getTrajectoryExpectationMode = (
  primitiveConfig: Record<string, unknown>,
): TrajectoryExpectationMode =>
  typeof primitiveConfig.expected_source === "string" && primitiveConfig.expected_source
    ? "column"
    : "tools";

export const getTrajectoryMatchMode = (
  primitiveConfig: Record<string, unknown>,
): TrajectoryMatchMode => (primitiveConfig.mode === "non_strict" ? "non_strict" : "strict");

const isToolNameList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(tool => typeof tool === "string");

export const getTrajectoryExpectedScenarios = (
  primitiveConfig: Record<string, unknown>,
): string[][] => {
  const {
    accepted_scenarios: acceptedScenarios,
    scenarios,
    expected_tools: expectedTools,
  } = primitiveConfig;
  if (Array.isArray(acceptedScenarios)) {
    const parsed = acceptedScenarios
      .filter(isToolNameList)
      .map(scenario => scenario.filter(tool => tool.trim().length > 0));
    if (parsed.length > 0) {
      return parsed;
    }
  }
  // Legacy keys from earlier trajectory config shapes
  if (Array.isArray(scenarios)) {
    const parsed = scenarios
      .filter(isToolNameList)
      .map(scenario => scenario.filter(tool => tool.trim().length > 0));
    if (parsed.length > 0) {
      return parsed;
    }
  }
  if (isToolNameList(expectedTools)) {
    const tools = expectedTools.filter(tool => tool.trim().length > 0);
    return tools.length > 0 ? [tools] : [[]];
  }
  return [[]];
};

export const getTrajectoryNoToolsExpected = (primitiveConfig: Record<string, unknown>): boolean => {
  const scenarios = primitiveConfig.accepted_scenarios;
  return (
    Array.isArray(scenarios) &&
    scenarios.length === 1 &&
    Array.isArray(scenarios[0]) &&
    scenarios[0].length === 0
  );
};

/** @deprecated Prefer getTrajectoryExpectedScenarios */
export const getTrajectoryExpectedTools = (primitiveConfig: Record<string, unknown>): string[] =>
  getTrajectoryExpectedScenarios(primitiveConfig)[0] ?? [];

export const getTrajectoryExpectedSource = (primitiveConfig: Record<string, unknown>): string => {
  const { expected_source: expectedSource } = primitiveConfig;
  return typeof expectedSource === "string" ? expectedSource : "";
};

export const getTrajectoryTraceSource = (
  primitiveConfig: Record<string, unknown>,
  fallbackSourceColumnId = "",
): string => {
  const { trace_source: traceSource } = primitiveConfig;
  return typeof traceSource === "string" && traceSource ? traceSource : fallbackSourceColumnId;
};
